/**
 * AdminCouponsScreen — gestión de cupones (Nilabs admin).
 *
 * Dos tipos:
 *   - Descuento (% o monto fijo): código que usan los seguidores del influencer
 *     en el checkout.
 *   - Meses gratis: código que el admin le da al influencer para que su cuenta
 *     quede con el plan activo N meses sin cobro (lo canjea en Configuración).
 */
import { useEffect, useState } from "react";
import { Icon } from "../../lib/Icon";
import { useToast } from "../../lib/toast";
import {
  listCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  type Coupon,
  type CouponKind,
  type DiscountType,
} from "../../api/coupons";

const PAID_TIERS = [
  { id: "solo", label: "Solo" },
  { id: "studio", label: "Studio" },
  { id: "unlimited", label: "Pro" },
];

function tierLabel(id: string | null): string {
  if (!id) return "Todos";
  return PAID_TIERS.find((t) => t.id === id)?.label || id;
}

export function AdminCouponsScreen() {
  const toast = useToast();
  const [coupons, setCoupons] = useState<Coupon[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const reload = () => {
    listCoupons()
      .then(setCoupons)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  };
  useEffect(reload, []);

  const toggleActive = async (c: Coupon) => {
    try {
      await updateCoupon(c.id, { active: !c.active });
      reload();
    } catch (e) {
      toast("No pude actualizar: " + (e instanceof Error ? e.message : ""));
    }
  };

  const remove = async (c: Coupon) => {
    if (!confirm(`¿Borrar el cupón ${c.code}?`)) return;
    try {
      await deleteCoupon(c.id);
      reload();
    } catch (e) {
      toast("No pude borrar: " + (e instanceof Error ? e.message : ""));
    }
  };

  return (
    <div className="al-admin-screen" style={{ maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 24 }}>Cupones</h1>
          <p className="tiny muted" style={{ marginTop: 2 }}>
            Descuentos para seguidores de influencers y meses gratis para colaboradores.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Icon name="plus" size={14} /> Nuevo cupón
        </button>
      </div>

      {err ? (
        <div className="al-ag-empty">No pude cargar los cupones: {err}</div>
      ) : !coupons ? (
        <div className="al-ag-empty">Cargando…</div>
      ) : coupons.length === 0 ? (
        <div className="al-ag-empty">
          <b>Sin cupones todavía</b>
          <p className="tiny muted">Creá el primero con "Nuevo cupón".</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {coupons.map((c) => (
            <div key={c.id} className="al-coupon-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <b style={{ fontFamily: "monospace", fontSize: 15 }}>{c.code}</b>
                  <span className={"al-coupon-badge " + (c.active ? "is-on" : "is-off")}>
                    {c.active ? "ACTIVO" : "INACTIVO"}
                  </span>
                </div>
                <div className="tiny muted" style={{ marginTop: 3 }}>
                  {c.kind === "discount"
                    ? `Descuento ${c.discount_type === "percent" ? `${c.discount_value}%` : `$${(c.discount_value || 0).toLocaleString("es-CO")} COP`} · plan ${tierLabel(c.applies_to_tier)}`
                    : `${c.free_months} mes(es) gratis · plan ${tierLabel(c.free_tier)}`}
                  {" · "}usos: {c.used_count}{c.max_uses != null ? `/${c.max_uses}` : ""}
                  {c.expires_at ? ` · vence ${c.expires_at}` : ""}
                  {c.note ? ` · ${c.note}` : ""}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(c)}>
                {c.active ? "Desactivar" : "Activar"}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => remove(c)} style={{ color: "var(--danger, #dc2626)" }}>
                <Icon name="x" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <CouponForm
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); reload(); toast("Cupón creado"); }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Form de creación
// ----------------------------------------------------------------------

function CouponForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [kind, setKind] = useState<CouponKind>("discount");
  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState<DiscountType>("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [freeMonths, setFreeMonths] = useState("2");
  const [freeTier, setFreeTier] = useState("unlimited");
  const [appliesTo, setAppliesTo] = useState<string>("");
  const [maxUses, setMaxUses] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!code.trim()) { toast("Falta el código"); return; }
    setBusy(true);
    try {
      await createCoupon({
        code: code.trim().toUpperCase(),
        kind,
        discount_type: kind === "discount" ? discountType : null,
        discount_value: kind === "discount" ? Number(discountValue) || 0 : null,
        free_months: kind === "free_months" ? Number(freeMonths) || 0 : null,
        free_tier: kind === "free_months" ? freeTier : null,
        applies_to_tier: appliesTo || null,
        max_uses: maxUses ? Number(maxUses) : null,
        expires_at: expiresAt || null,
        note: note.trim() || null,
        active: true,
      });
      onCreated();
    } catch (e) {
      toast("No pude crear el cupón: " + (e instanceof Error ? e.message : ""));
      setBusy(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="al-modal" style={{ maxWidth: 480, width: "94vw" }} onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-hd">
          <h3 style={{ margin: 0, fontSize: 16 }}>Nuevo cupón</h3>
          <button className="icon-btn" onClick={onClose} disabled={busy}><Icon name="x" size={16} /></button>
        </div>
        <div className="al-modal-body" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
          <div className="al-ag-segment">
            <button type="button" className={kind === "discount" ? "is-on" : ""} onClick={() => setKind("discount")}>Descuento</button>
            <button type="button" className={kind === "free_months" ? "is-on" : ""} onClick={() => setKind("free_months")}>Meses gratis</button>
          </div>

          <label className="al-ag-field">
            <span className="al-ag-flabel">Código</span>
            <input className="al-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="EJ. INFLU20" style={{ textTransform: "uppercase" }} />
          </label>

          {kind === "discount" ? (
            <div className="al-ag-row2">
              <label className="al-ag-field">
                <span className="al-ag-flabel">Tipo</span>
                <select className="al-input" value={discountType} onChange={(e) => setDiscountType(e.target.value as DiscountType)}>
                  <option value="percent">Porcentaje (%)</option>
                  <option value="fixed">Monto fijo (COP)</option>
                </select>
              </label>
              <label className="al-ag-field">
                <span className="al-ag-flabel">{discountType === "percent" ? "Porcentaje" : "Monto COP"}</span>
                <input className="al-input" type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} placeholder={discountType === "percent" ? "20" : "20000"} />
              </label>
            </div>
          ) : (
            <div className="al-ag-row2">
              <label className="al-ag-field">
                <span className="al-ag-flabel">Meses gratis</span>
                <input className="al-input" type="number" value={freeMonths} onChange={(e) => setFreeMonths(e.target.value)} />
              </label>
              <label className="al-ag-field">
                <span className="al-ag-flabel">Plan otorgado</span>
                <select className="al-input" value={freeTier} onChange={(e) => setFreeTier(e.target.value)}>
                  {PAID_TIERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </label>
            </div>
          )}

          {kind === "discount" && (
            <label className="al-ag-field">
              <span className="al-ag-flabel">Aplica al plan</span>
              <select className="al-input" value={appliesTo} onChange={(e) => setAppliesTo(e.target.value)}>
                <option value="">Todos los planes pagos</option>
                {PAID_TIERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
          )}

          <div className="al-ag-row2">
            <label className="al-ag-field">
              <span className="al-ag-flabel">Tope de usos (opcional)</span>
              <input className="al-input" type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="ilimitado" />
            </label>
            <label className="al-ag-field">
              <span className="al-ag-flabel">Vence (opcional)</span>
              <input className="al-input" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </label>
          </div>

          <label className="al-ag-field">
            <span className="al-ag-flabel">Nota (opcional)</span>
            <input className="al-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej. Influencer Juan Pérez" />
          </label>
        </div>
        <div className="al-modal-ft" style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: 16, flexShrink: 0, borderTop: "1px solid var(--border)" }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !code.trim()}>
            {busy ? "Creando…" : "Crear cupón"}
          </button>
        </div>
      </div>
    </div>
  );
}
